// Строка голосового в логе:
//
//   ♪ голосовое 5:56 · расшифровка сохранена   показать · в поле ввода
//
// Строку пишет сервер, когда расшифровка уже лежит на диске
// (voice_store.py), поэтому текст достаётся отсюда всегда — даже если до
// агента он так и не доехал. Текст грузится по кнопке, а не сразу: в логе
// он и так появится репликой «> …», дублировать его не нужно.

import { memo, useCallback, useState } from 'react'
import { api } from '../api'
import { useLogStore } from '../store/logStore'

interface Props {
  sessionId: string
  voiceId: string
  text: string
  /** телефон: кнопки крупнее, без моноширинной сетки */
  touch?: boolean
}

function VoiceLine({ sessionId, voiceId, text, touch = false }: Props) {
  const setDraft = useLogStore((s) => s.setDraft)
  const [body, setBody] = useState<string | null>(null)
  const [open, setOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)

  const load = useCallback(async (): Promise<string | null> => {
    if (body !== null) return body
    try {
      const item = await api.voiceGet(sessionId, voiceId)
      setBody(item.text)
      setError(null)
      return item.text
    } catch (err) {
      setError(err instanceof Error ? err.message : 'не удалось достать расшифровку')
      return null
    }
  }, [body, sessionId, voiceId])

  const toggle = useCallback(async () => {
    if (open) { setOpen(false); return }
    if (await load()) setOpen(true)
  }, [open, load])

  const toDraft = useCallback(async () => {
    const value = await load()
    if (!value) return
    setDraft(sessionId, value)
    setDone('текст в поле ввода')
  }, [load, sessionId, setDraft])

  const copy = useCallback(async () => {
    const value = await load()
    if (!value) return
    try {
      await navigator.clipboard.writeText(value)
      setDone('скопировано')
    } catch {
      setOpen(true) // буфер недоступен — хотя бы показать, чтобы выделить руками
    }
  }, [load])

  return (
    <div className={`t-row t-row--voice t-row--head t-voice${touch ? ' t-voice--touch' : ''}`}>
      <span className="t-gut">♪</span>
      <span className="t-txt">
        {text}
        <span className="t-voice__acts">
          <button type="button" className="t-more" onClick={toggle}>
            {open ? 'скрыть' : 'показать'}
          </button>
          {' · '}
          <button type="button" className="t-more" onClick={toDraft}>в поле ввода</button>
          {' · '}
          <button type="button" className="t-more" onClick={copy}>копировать</button>
          {done ? <span className="t-voice__done">{' · '}{done}</span> : null}
        </span>
        {error ? <span className="t-voice__err">{error}</span> : null}
        {open && body !== null ? <span className="t-voice__body">{body}</span> : null}
      </span>
    </div>
  )
}

export default memo(VoiceLine)
